import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, Tooltip,
} from "recharts";
import { AlertTriangle, ArrowRight } from "lucide-react";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--warning))",
  medium:   "hsl(var(--primary))",
  low:      "hsl(var(--muted-foreground))",
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const SeverityDonut = ({ data = [] }) => {
  const filtered = data.filter((d) => d.count > 0);
  const total = data.reduce((sum, d) => sum + d.count, 0);
  return (
    <div className="relative h-40" data-testid="alerts-severity-donut">
      {filtered.length === 0 ? (
        <div className="h-full flex items-center justify-center text-[11px] text-muted-foreground">
          No alerts in window.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={filtered}
              dataKey="count"
              nameKey="severity"
              innerRadius={42}
              outerRadius={62}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {filtered.map((d) => (
                <Cell key={d.severity} fill={SEV_COLOR[d.severity]} stroke="hsl(var(--surface))" strokeWidth={1.5} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "hsl(var(--surface-elevated))",
                border: "1px solid hsl(var(--border))",
                fontSize: 11, padding: 6, borderRadius: 2,
              }}
              formatter={(v, n) => [`${v}`, titleCase(n)]}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-mono text-[9px] text-muted-foreground tracking-wide">Total</div>
        <div className="font-display font-black text-xl leading-none" data-testid="alerts-severity-total">{total}</div>
      </div>
    </div>
  );
};

const AlertTrend = ({ data = [] }) => {
  const series = data.map((d) => ({
    t: new Date(d.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    count: d.count,
  }));
  return (
    <div className="h-40" data-testid="alerts-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 10, left: -22, bottom: 0 }}>
          <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} interval="preserveStartEnd" />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} allowDecimals={false} width={26} />
          <Tooltip
            contentStyle={{
              background: "hsl(var(--surface-elevated))",
              border: "1px solid hsl(var(--border))",
              fontSize: 11, padding: 6, borderRadius: 2,
            }}
          />
          <Line
            type="monotone"
            dataKey="count"
            stroke="hsl(var(--primary))"
            strokeWidth={1.75}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const TopCategoriesBar = ({ data = [] }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="space-y-1.5" data-testid="alerts-top-types">
      {data.length === 0 && (
        <div className="text-[11px] text-muted-foreground">No alert categories in window.</div>
      )}
      {data.map((row) => {
        const w = Math.round((row.count / max) * 100);
        return (
          <div key={row.type} className="flex items-center gap-2 text-[11px]" data-testid={`alerts-type-${row.type}`}>
            <div className="w-32 truncate text-foreground">{titleCase(row.type)}</div>
            <div className="flex-1 h-2 bg-[hsl(var(--muted))] relative">
              <div
                className="absolute inset-y-0 left-0 bg-[hsl(var(--primary))]"
                style={{ width: `${w}%` }}
              />
            </div>
            <div className="w-6 text-right font-mono text-[10px] text-muted-foreground">{row.count}</div>
          </div>
        );
      })}
    </div>
  );
};

export const AlertsIntelligencePanel = ({ data, loading }) => {
  const navigate = useNavigate();
  const alerts = data || { by_severity: [], trend: [], top_types: [], recent: [] };

  return (
    <section className="space-y-3" data-testid="dashboard-section-alerts">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold tracking-tight text-xs">Alerts Intelligence</h2>
        </div>
        <button
          onClick={() => navigate("/incidents")}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="alerts-open-incidents-btn"
        >
          Open Incidents <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2">Severity Distribution</div>
          <SeverityDonut data={alerts.by_severity} />
          <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
            {alerts.by_severity.map((s) => (
              <span key={s.severity} className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground" data-testid={`alerts-sev-legend-${s.severity}`}>
                <span className="w-2 h-2" style={{ background: SEV_COLOR[s.severity] }} />
                {titleCase(s.severity)} · {s.count}
              </span>
            ))}
          </div>
        </div>

        <div className="panel p-3">
          <div className="h-widget mb-2">Alert Trend</div>
          <AlertTrend data={alerts.trend} />
        </div>

        <div className="panel p-3">
          <div className="h-widget mb-2">Top Categories</div>
          <TopCategoriesBar data={alerts.top_types} />
        </div>
      </div>

      {/* Live alert feed */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget">Live Alert Feed</div>
          <span className="micro-label">Latest 5 Active</span>
        </div>
        {alerts.recent.length === 0 ? (
          <div className="text-[11px] text-muted-foreground py-3 text-center">No active alerts.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="alerts-feed-table">
              <thead>
                <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-wide border-b border-border">
                  <th className="py-1.5 px-2 font-medium">Time</th>
                  <th className="py-1.5 px-2 font-medium">Alert</th>
                  <th className="py-1.5 px-2 font-medium">Zone</th>
                  <th className="py-1.5 px-2 font-medium">Severity</th>
                  <th className="py-1.5 px-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {alerts.recent.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
                    data-testid={`alerts-feed-row-${row.id}`}
                  >
                    <td className="py-1.5 px-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                      {relTime(row.ts)}
                    </td>
                    <td className="py-1.5 px-2 truncate max-w-[260px]">{row.title}</td>
                    <td className="py-1.5 px-2 text-muted-foreground truncate">{row.zone_name}</td>
                    <td className="py-1.5 px-2">
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold tracking-wide border"
                        style={{ color: SEV_COLOR[row.severity], borderColor: SEV_COLOR[row.severity] }}
                      >
                        {titleCase(row.severity)}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <button
                        className="text-[10px] text-[hsl(var(--primary))] hover:underline"
                        onClick={() => navigate("/incidents")}
                        data-testid={`alerts-feed-action-${row.id}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AlertsIntelligencePanel;
